import {
  PROPERTY_PAGE_SIZE_MAX,
  type ApiClient,
  type Property as ApiProperty,
  type PropertyClass,
  type PropertyCreateRequest,
  type PropertyDetailsRequest,
  type PropertyListQuery,
  type PropertyRecord,
  type PropertyStructure,
  type PropertyType,
} from "@shyne/shared/api";
import type { Property } from "@/models/properties.model";
import type { User } from "@/models/user.model";

export type PropertyContactCard = Pick<User, "name" | "phone" | "email" | "image"> & { description?: string | null };

export interface PropertyWithRelations {
  property: Property;
  unit_count: number;
  appointmentCount: number;
  property_manager: PropertyContactCard | null;
}

export function toProperty(property: ApiProperty): Property {
  const details = property.details;
  return {
    id: property.id,
    name: property.name,
    address: property.address ?? "",
    meta_data: property.accessNotes === null ? {} : { access_notes: property.accessNotes },
    organization: property.customer?.id ?? null,
    image: details?.image ?? "",
    street: details?.street ?? "",
    city: details?.city ?? "",
    county: details?.county ?? "",
    state: details?.state ?? "",
    zip: details?.zip ?? "",
    latitude: details?.latitude ?? null,
    longitude: details?.longitude ?? null,
    description: details?.description ?? "",
    website: details?.website ?? "",
    type: details?.types ?? [],
    structure: details?.structures ?? [],
    class: details?.classes ?? [],
    year_built: details?.yearBuilt ?? null,
    floors: details?.floors ?? null,
    bill_to_name: property.billTo?.name ?? "",
    bill_to_address: property.billTo?.address ?? "",
    created_at: details?.createdAt ?? null,
    deleted_at: null,
  } as Property;
}

export function toPropertyWithRelations(record: PropertyRecord): PropertyWithRelations {
  const contact = record.leadContact ?? record.onSiteContact ?? null;
  return {
    property: toProperty(record),
    unit_count: record.unitCount ?? 0,
    appointmentCount: record.appointmentCount ?? 0,
    property_manager: contact === null ? null : { name: contact.name, phone: contact.phone ?? "", email: contact.email ?? "", image: contact.image ?? "" },
  };
}

type ListProperties = ApiClient["properties"]["list"];

export async function everyProperty(list: ListProperties, query: Partial<PropertyListQuery>): Promise<ApiProperty[]> {
  const properties: ApiProperty[] = [];
  for (let page = 1; ; page++) {
    const { items, total } = await list({ ...query, page, pageSize: PROPERTY_PAGE_SIZE_MAX });
    properties.push(...items);
    if (items.length < PROPERTY_PAGE_SIZE_MAX || properties.length >= total) return properties;
  }
}

type PropertyForm = Partial<Omit<Property, "zip" | "latitude" | "longitude">> & { zip?: string | number | null; latitude?: number | null; longitude?: number | null };

const textOrNull = (value: string | null | undefined): string | null => (value === undefined || value === null || value.trim() === "" ? null : value);

export function toDetailsRequest(values: PropertyForm): PropertyDetailsRequest {
  return {
    name: values.name ?? "",
    description: textOrNull(values.description),
    yearBuilt: Number(values.year_built),
    floors: Number(values.floors),
    types: (values.type ?? []) as PropertyType[],
    structures: (values.structure ?? []) as PropertyStructure[],
    classes: (values.class ?? []) as PropertyClass[],
    billToName: textOrNull(values.bill_to_name),
    billToAddress: textOrNull(values.bill_to_address),
  };
}

export function toCreateRequest(customer: string, values: PropertyForm): PropertyCreateRequest {
  return {
    ...toDetailsRequest(values),
    customer,
    address: values.address ?? "",
    website: textOrNull(values.website),
    street: textOrNull(values.street),
    city: textOrNull(values.city),
    county: textOrNull(values.county),
    state: textOrNull(values.state),
    zip: values.zip === undefined || values.zip === null ? null : textOrNull(String(values.zip)),
    latitude: values.latitude ?? null,
    longitude: values.longitude ?? null,
  };
}
